import { UserIcon, Mail, Lock, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { AuthInput } from './AuthInput';
import { AuthLayout } from './AuthLayout';
import { 
  createUserWithEmailAndPassword, 
  updateProfile, 
  sendEmailVerification,
  signOut,
  linkWithCredential, 
  EmailAuthProvider
} from 'firebase/auth';

export const Register = ({ isLight, onNavigate, onLoginSuccess, db, appId, auth }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false); // New state to track email status

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) return;
    setLoading(true);
    setError('');

    try {
      // 1. Create User in Firebase Authentication
      let user;
      const credential = EmailAuthProvider.credential(email, password);

      // CHECK: If user is currently anonymous, LINK the account
      if (auth.currentUser?.isAnonymous) {
        const userCredential = await linkWithCredential(auth.currentUser, credential);
        user = userCredential.user;
      } else {
        // Fallback: If not anonymous, create a fresh account
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        user = userCredential.user;
      }

      // 2. Add the User's Name to their Auth Profile (but not to DB yet)
      await updateProfile(user, { displayName: name });

      // 3. Trigger Email Verification
      await sendEmailVerification(user);
      
      // 4. IMPORTANT: Sign the user out immediately
      await signOut(auth);

      // 5. Update UI to show the "Check your email" message
      setVerificationSent(true);

    } catch (err) {
      console.error(err);
      let errorMessage = '';
      if (err.message.includes('email-already-in-use') ) {
        errorMessage = 'This email is already registered.';
      } else if (err.message.includes('at least') && err.message.includes('6 characters') && password.length < 6) {
        errorMessage = 'Password must be at least 6 characters.';
      } else {
        errorMessage = 'Registration failed. Try again later.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // UI for when the email has been sent
  if (verificationSent) {
    return (
      <AuthLayout 
        title="Check your inbox" 
        subtitle={`We've sent a verification link to ${email}`}
        isLight={isLight}
        modeText="Back to login?"
        modeAction="Sign in"
        onToggleMode={() => onNavigate('/login')}
      >
        <div className="text-center py-8">
          <div className="bg-cyan-500/10 w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <Mail className="text-cyan-500" size={32} />
          </div>
          <p className={`text-sm mb-6 ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
            Please click the link in your email to verify your account. Once verified, you can sign in.
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout 
      title="Create Account" 
      subtitle="Join the network for advanced protection"
      isLight={isLight}
      modeText="Already have an account?"
      modeAction="Sign in"
      onToggleMode={() => onNavigate('/login')}
    >
      <form onSubmit={handleRegister}>
        <AuthInput label="Your Name" type="text" icon={UserIcon} placeholder="John Doe" value={name} onChange={setName} isLight={isLight} />
        <AuthInput label="Email Address" type="email" icon={Mail} placeholder="name@email.com" value={email} onChange={setEmail} isLight={isLight} />
        <AuthInput label="Choose Password" type="password" icon={Lock} placeholder="Min. 8 characters" value={password} onChange={setPassword} isLight={isLight} />
        
        {error && <p className="text-red-500 text-[10px] mb-4 uppercase tracking-wider">{error}</p>}

        <button 
          disabled={loading}
          type="submit"
          className={`w-full mt-4 font-black py-5 rounded uppercase text-xs tracking-[0.2em] flex items-center justify-center gap-2 transition-all ${
            loading ? 'opacity-50 cursor-not-allowed' : ''
          } ${isLight ? 'bg-cyan-500 text-white hover:bg-cyan-600' : 'bg-cyan-500 text-white hover:bg-cyan-400 shadow-[0_0_20px_rgba(6,182,212,0.3)]'}`}
        >
          {loading ? 'Creating Profile...' : 'Create Profile'} <ShieldCheck size={14} />
        </button>
      </form>
    </AuthLayout>
  );
};